// ═══════════════════════════════════════════════════════════════
// APP.JSX - Actualizado para multi-tenant
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import Login from './components/Login/Login';
import SevenFamilyHR from './SevenFamilyHR';
import { authAPI } from './api/auth';
import { CompanyProvider } from './contexts/CompanyContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import MigrationPanel from './components/MigrationPanel';
import ActivateAccount from './components/Auth/ActivateAccount';

export default function App() {
  const pathname = window.location.pathname;
  console.log('Current path:', pathname);

  if (pathname === '/migrate') {
    return <MigrationPanel />;
  }

  if (pathname === '/activar-cuenta') {
    return <ActivateAccount />;
  }

  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('🚀 App iniciando...');

    // Verificar si es la primera carga de esta sesión del navegador
    const hasActiveSession = sessionStorage.getItem('hasActiveSession');

    if (!hasActiveSession) {
      // Es la primera carga → limpiar sesión y mostrar login
      console.log('🔐 Primera carga - requiere login');
      authAPI.logout();
      setCurrentUser(null);
      setIsLoading(false);
    } else {
      // Ya hay una sesión activa en esta pestaña → verificar si el usuario está logueado
      console.log('🔄 Sesión activa detectada - verificando usuario');
      const savedUser = authAPI.getCurrentUser();
      const token = localStorage.getItem('token');

      if (savedUser && token) {
        console.log('✅ Usuario recuperado:', savedUser.username);
        setCurrentUser(savedUser);
      } else {
        console.log('⚠️ No hay usuario guardado - requiere login');
      }
      setIsLoading(false);
    }
  }, []);

  const handleLogin = async (username, password) => {
    console.log('🔐 Intento de login:', username);

    try {
      const data = await authAPI.login(username, password);
      console.log('✅ Login exitoso:', data.user.username);

      // Marcar que hay una sesión activa en esta pestaña del navegador
      sessionStorage.setItem('hasActiveSession', 'true');

      setCurrentUser(data.user);
      return true;
    } catch (error) {
      console.error('❌ Error en login:', error);
      if (error.response?.status === 429) {
        throw new Error('Demasiados intentos fallidos. Acceso bloqueado temporalmente. Intenta de nuevo en 15 minutos.');
      } else if (error.response?.status === 401) {
        return false;
      } else if (error.code === 'ERR_NETWORK') {
        throw new Error('No se puede conectar al servidor. Verifica tu conexión.');
      } else {
        throw new Error(error.response?.data?.error || 'Error inesperado al iniciar sesión.');
      }
    }
  };

  const handleLogout = () => {
    console.log('👋 Cerrando sesión...');
    authAPI.logout();

    // currentCompanyId se conserva para restaurar la última compañía en el próximo login

    // Limpiar la marca de sesión activa
    sessionStorage.removeItem('hasActiveSession');

    // NO limpiar credenciales recordadas - deben persistir entre sesiones
    // localStorage.removeItem('rememberedUsername');
    // localStorage.removeItem('rememberedPassword');

    setCurrentUser(null);
    console.log('✅ Sesión cerrada');
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg font-semibold">Cargando 7Family HR...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!currentUser) {
    console.log('🔓 Mostrando pantalla de login');
    return <Login onLogin={handleLogin} />;
  }

  // Show main app if authenticated - WRAPPED with CompanyProvider
  console.log('🏠 Mostrando dashboard para:', currentUser.username);
  return (
    <PermissionsProvider currentUser={currentUser}>
      <CompanyProvider>
        <SevenFamilyHR currentUser={currentUser} onLogout={handleLogout} />
      </CompanyProvider>
    </PermissionsProvider>
  );
}
